// src/search/useRecentSearches.ts
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import type { RecentSearch } from "./types";
import { RECENT_SEARCHES_KEY } from "./constants";

export interface UseRecentSearchesReturn {
  // Query state
  recentSearches: RecentSearch[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  hasData: boolean;

  // Mutations
  pinSearch: (id: string) => void;
  unpinSearch: (id: string) => void;
  isPinning: boolean;
  isUnpinning: boolean;

  // UI state
  expandedIds: string[];
  toggleExpanded: (id: string) => void;

  // Invalidation
  invalidateRecentSearches: () => void;
}

export function useRecentSearches(): UseRecentSearchesReturn {
  const queryClient = useQueryClient();
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const {
    data: recentSearches,
    isLoading,
    isError,
    isFetching,
  } = useQuery<RecentSearch[]>({
    queryKey: RECENT_SEARCHES_KEY,
    queryFn: async () => {
      const res = await api.get("searches/recent/");
      return res.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const pinMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`searches/${id}/pin/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECENT_SEARCHES_KEY });
    },
  });

  const unpinMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`searches/${id}/unpin/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECENT_SEARCHES_KEY });
    },
  });

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const invalidateRecentSearches = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: RECENT_SEARCHES_KEY });
  }, [queryClient]);

  const searches = recentSearches ?? [];

  return {
    recentSearches: searches,
    isLoading,
    isError,
    isFetching,
    hasData: searches.length > 0,
    pinSearch: pinMutation.mutate,
    unpinSearch: unpinMutation.mutate,
    isPinning: pinMutation.isPending,
    isUnpinning: unpinMutation.isPending,
    expandedIds,
    toggleExpanded,
    invalidateRecentSearches,
  };
}

/**
 * Hook to invalidate recent searches after a search completes.
 */
export function useInvalidateRecentSearchesOnFetch(
  isFetched: boolean,
  hasParams: boolean
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isFetched && hasParams) {
      const handle = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: RECENT_SEARCHES_KEY });
      }, 300);
      return () => clearTimeout(handle);
    }
  }, [hasParams, isFetched, queryClient]);
}
